import React from 'react';
import {
    CheckCircle2,
    XCircle,
    MinusCircle,
    GitBranch,
    ExternalLink,
    ShieldCheck,
} from 'lucide-react';
import type { MetaBuildResult, MetaBuildStage } from '../../stores/useChatStore.js';
import { openInContextCanvas } from '../../studio/canvas/index.js';

interface MetaBuildResultCardProps {
    data: MetaBuildResult;
    className?: string;
}

function normalizeStageStatus(
    status: string | undefined
): 'success' | 'failed' | 'skipped' | 'unknown' {
    const s = (status || '').toLowerCase();
    if (s === 'success' || s === 'succeeded' || s === 'ok') return 'success';
    if (s === 'failed' || s === 'error') return 'failed';
    if (s === 'skipped') return 'skipped';
    return 'unknown';
}

function stageBadge(status: 'success' | 'failed' | 'skipped' | 'unknown'): {
    icon: React.ReactNode;
    className: string;
    label: string;
} {
    switch (status) {
        case 'success':
            return {
                icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />,
                className: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
                label: 'success',
            };
        case 'failed':
            return {
                icon: <XCircle className="w-3.5 h-3.5 text-red-400" />,
                className: 'bg-red-500/10 border-red-500/30 text-red-300',
                label: 'failed',
            };
        case 'skipped':
            return {
                icon: <MinusCircle className="w-3.5 h-3.5 text-slate-400" />,
                className: 'bg-slate-500/10 border-slate-500/20 text-slate-300',
                label: 'skipped',
            };
        default:
            return {
                icon: <MinusCircle className="w-3.5 h-3.5 text-slate-500" />,
                className: 'bg-slate-700/20 border-slate-600/30 text-slate-300',
                label: 'unknown',
            };
    }
}

function getQualityBadge(qualityGatePassed: boolean | null | undefined): {
    className: string;
    label: string;
} {
    if (qualityGatePassed === true) {
        return {
            className: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
            label: 'Quality: PASS',
        };
    }
    if (qualityGatePassed === false) {
        return {
            className: 'bg-red-500/10 border-red-500/30 text-red-300',
            label: 'Quality: FAIL',
        };
    }
    return {
        className: 'bg-slate-700/20 border-slate-600/30 text-slate-300',
        label: 'Quality: unknown',
    };
}

function safeStages(stages: unknown): MetaBuildStage[] {
    if (!Array.isArray(stages)) return [];
    return stages.filter(s => s && typeof s === 'object') as MetaBuildStage[];
}

export const MetaBuildResultCard: React.FC<MetaBuildResultCardProps> = ({
    data,
    className = '',
}) => {
    const isSuccess = data.success === true;
    const prUrl = typeof data.prUrl === 'string' && data.prUrl.length > 0 ? data.prUrl : null;
    const branchName =
        typeof data.branchName === 'string' && data.branchName.length > 0 ? data.branchName : null;
    const quality = getQualityBadge(
        typeof data.qualityGatePassed === 'boolean'
            ? data.qualityGatePassed
            : (data.qualityGatePassed ?? null)
    );
    const stages = safeStages(data.stages);
    const handleViewPrClick = (e: React.MouseEvent) => {
        if (!prUrl) return;
        const routed = openInContextCanvas({
            type: 'browser',
            url: prUrl,
            title: 'Pull Request',
        });
        if (routed) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    return (
        <div className={`relative overflow-hidden ${className}`}>
            <div className="relative bg-slate-900/60 backdrop-blur-xl border border-slate-700/40 rounded-2xl shadow-xl shadow-black/30">
                {/* Header */}
                <div className="px-5 py-4 border-b border-slate-700/30 flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-slate-800/50 border border-slate-700/50 flex items-center justify-center flex-shrink-0">
                            {isSuccess ? (
                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                            ) : (
                                <XCircle className="w-5 h-5 text-red-400" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-slate-100">
                                    MetaBuild {isSuccess ? 'Succeeded' : 'Failed'}
                                </span>
                                {data.dryRun === true && (
                                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-600/40 bg-slate-800/40 text-slate-300">
                                        dry-run
                                    </span>
                                )}
                                <span
                                    className={`text-[10px] px-2 py-0.5 rounded-full border ${quality.className}`}
                                >
                                    <span className="inline-flex items-center gap-1">
                                        <ShieldCheck className="w-3 h-3" />
                                        {quality.label}
                                    </span>
                                </span>
                            </div>
                            {(data.targetName || data.specPath) && (
                                <div className="text-[11px] text-slate-500 mt-1 truncate">
                                    {data.targetName ? `Target: ${String(data.targetName)}` : null}
                                    {data.targetName && data.specPath ? ' • ' : null}
                                    {data.specPath ? `Spec: ${String(data.specPath)}` : null}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* PR link */}
                    {prUrl && (
                        <a
                            href={prUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleViewPrClick}
                            className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600/20 hover:bg-green-600/30 text-green-300 text-sm transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            View PR
                        </a>
                    )}
                </div>

                {/* Body */}
                <div className="px-5 py-4 space-y-4">
                    {/* Branch */}
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-xs text-slate-500 mb-1">Branch</div>
                            {branchName ? (
                                <div className="inline-flex items-center gap-2 text-xs text-slate-200 font-mono bg-slate-800/40 border border-slate-700/40 rounded-lg px-2.5 py-1.5">
                                    <GitBranch className="w-3.5 h-3.5 text-slate-400" />
                                    <span className="truncate">{branchName}</span>
                                </div>
                            ) : (
                                <div className="text-xs text-slate-400">—</div>
                            )}
                        </div>
                    </div>

                    {/* Stages */}
                    <div>
                        <div className="text-xs text-slate-500 mb-2">Stages</div>
                        {stages.length === 0 ? (
                            <div className="text-xs text-slate-400">No stage data.</div>
                        ) : (
                            <div className="space-y-2">
                                {stages.map((stage, idx) => {
                                    const status = normalizeStageStatus(
                                        typeof stage.status === 'string' ? stage.status : undefined
                                    );
                                    const badge = stageBadge(status);
                                    // Backend sends 'stage' field, UI type has 'name' - support both
                                    const stageName =
                                        (stage as any).stage || stage.name || 'unknown';
                                    return (
                                        <div
                                            key={`${String(stageName)}_${idx}`}
                                            className="flex items-center justify-between gap-3"
                                        >
                                            <div className="text-xs text-slate-200 font-mono truncate">
                                                {String(stageName)}
                                            </div>
                                            <div
                                                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] ${badge.className}`}
                                            >
                                                {badge.icon}
                                                <span className="uppercase tracking-wide">
                                                    {badge.label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MetaBuildResultCard;
